import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { toast } from 'sonner';
import { 
  AlertTriangle,
  RefreshCw,
  Clock,
  MessageSquare,
  UserX,
  AlertCircle,
  MessageCircleWarning,
  Star,
  ChevronRight,
  ChevronDown,
  Loader,
  Store,
  User,
  CreditCard,
  FileText,
  Flame,
  CheckCircle,
  X,
  Mail,
  ExternalLink,
  Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'celey:admin:attention:expandedSections';
const FILTER_STORAGE_KEY = 'celey:admin:attention:queueFilter';

// Severity thresholds (hours)
const SEVERITY = {
  CRITICAL: 72,  // 3+ days = critical
  HIGH: 48,      // 2+ days = high
};

// Default expanded state
const DEFAULT_EXPANDED = {
  stalled_inquiry: true,
  concierge_unclaimed: true,
  vendor_onboarding: true,
  dispute: true,
  feedback: true,
  pending_review: true
};

// Load persisted state from localStorage
const loadExpandedState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_EXPANDED, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error loading expanded state:', e);
  }
  return DEFAULT_EXPANDED;
};

const AdminAttentionTab = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(loadExpandedState);
  // Concierge Queue filter chip — additive, only meaningful when the backend
  // surfaces concierge_unclaimed items (controlled by ENABLE_CONCIERGE_FLOW).
  const [queueFilter, setQueueFilter] = useState(() => {
    try {
      return localStorage.getItem(FILTER_STORAGE_KEY) || 'all';
    } catch (e) {
      return 'all';
    }
  });

  const fetchAttentionItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/attention-needed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(response.data.items || []);
      setCounts(response.data.counts || {});
    } catch (error) {
      console.error('Error fetching attention items:', error);
      toast.error('Failed to load attention items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttentionItems();
  }, [fetchAttentionItems]);

  // Persist expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedSections));
    } catch (e) {
      console.error('Error saving expanded state:', e);
    }
  }, [expandedSections]);

  // Persist queue filter selection
  useEffect(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, queueFilter);
    } catch (e) {
      /* localStorage unavailable — ignore */
    }
  }, [queueFilter]);

  const toggleSection = (type) => {
    setExpandedSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const formatTimeAgo = (hours) => {
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  // Get severity level based on hours
  const getSeverity = (hours) => {
    if (hours >= SEVERITY.CRITICAL) return 'critical';
    if (hours >= SEVERITY.HIGH) return 'high';
    return 'normal';
  };

  // Get hours from item based on type
  const getItemHours = (item) => {
    return item.hours_since_response || item.hours_since_submitted || item.hours_since_intake || (item.days_since_signup * 24) || 0;
  };

  // Dismiss an attention item
  const dismissItem = async (e, itemId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/attention-needed/${itemId}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Remove from local state immediately
      setItems(prev => prev.filter(item => item.id !== itemId));
      setCounts(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      toast.success('Item dismissed');
    } catch (error) {
      console.error('Error dismissing item:', error);
      toast.error('Failed to dismiss item');
    }
  };

  const handleItemClick = (item) => {
    switch (item.type) {
      case 'stalled_inquiry':
        setSearchParams({ tab: 'active-inquiries' });
        break;
      case 'vendor_onboarding':
        navigate(`/admin/dashboard?tab=users&search=${encodeURIComponent(item.vendor_name)}`);
        break;
      case 'dispute':
        setSearchParams({ tab: 'disputes' });
        break;
      case 'feedback':
        setSearchParams({ tab: 'feedback' });
        break;
      case 'pending_review':
        setSearchParams({ tab: 'reviews' });
        break;
      case 'concierge_unclaimed':
        // Phase A.5 — Admin Navigation Stabilization (permanent).
        // Concierge Queue cards deep-link directly into the Concierge
        // Admin workspace at /admin/concierge?event=<event_id>. The
        // workspace honours that query param and auto-opens the row's
        // drawer. This is the supported long-term deep-link pattern
        // for opening a specific Concierge request — used by the
        // Attention tab today and by any future surface (alerts,
        // emails, push notifications) that needs to point an admin
        // at a single request.
        if (item.event_id) {
          navigate(`/admin/concierge?event=${encodeURIComponent(item.event_id)}`);
        } else {
          navigate('/admin/concierge');
        }
        break;
      default:
        break;
    }
  };

  const getCategoryConfig = (type) => {
    switch (type) {
      case 'stalled_inquiry':
        return {
          title: 'Stalled Inquiries',
          icon: MessageSquare,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          description: 'Customer messages awaiting vendor response'
        };
      case 'vendor_onboarding':
        return {
          title: 'Vendor Onboarding Issues',
          icon: UserX,
          iconColor: 'text-orange-500',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          description: 'Incomplete profiles or Stripe setup'
        };
      case 'dispute':
        return {
          title: 'Disputes',
          icon: AlertCircle,
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          description: 'Unresolved customer disputes'
        };
      case 'feedback':
        return {
          title: 'Unreviewed Feedback',
          icon: MessageCircleWarning,
          iconColor: 'text-amber-500',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          description: 'User feedback awaiting review'
        };
      case 'pending_review':
        return {
          title: 'Pending Reviews',
          icon: Star,
          iconColor: 'text-blue-500',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          description: 'Reviews awaiting approval'
        };
      case 'concierge_unclaimed':
        return {
          title: 'Concierge Queue',
          icon: Sparkles,
          iconColor: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          description: 'Unclaimed Concierge events awaiting a worker'
        };
      default:
        return {
          title: 'Unknown',
          icon: AlertTriangle,
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          description: ''
        };
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          border: 'border-l-4 border-l-red-500',
          bg: 'bg-red-50/50',
          badge: 'bg-red-100 text-red-700'
        };
      case 'high':
        return {
          border: 'border-l-4 border-l-amber-500',
          bg: 'bg-amber-50/30',
          badge: 'bg-amber-100 text-amber-700'
        };
      default:
        return {
          border: '',
          bg: 'bg-white',
          badge: ''
        };
    }
  };

  const renderStalledInquiry = (item, severity) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-sm text-gray-600">{item.status_label}</span>
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Store className="h-4 w-4 text-purple-500" />
        <span className="font-medium">{item.vendor_name}</span>
        <span className="text-gray-400">→</span>
        <User className="h-4 w-4 text-blue-500" />
        <span>{item.customer_name}</span>
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>{formatTimeAgo(item.hours_since_response)} since last vendor response</span>
      </div>
    </div>
  );

  const renderVendorOnboarding = (item, severity) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Store className="h-4 w-4 text-purple-500" />
        <span className="font-medium">{item.vendor_name}</span>
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {item.profile_incomplete && (
          <Badge variant="outline" className="text-xs bg-gray-50">
            <FileText className="h-3 w-3 mr-1" />
            Profile incomplete
          </Badge>
        )}
        {item.stripe_status !== 'complete' && (
          <Badge variant="outline" className="text-xs bg-gray-50">
            <CreditCard className="h-3 w-3 mr-1" />
            Stripe {item.stripe_status === 'not_started' ? 'not started' : 'incomplete'}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>Signed up {item.days_since_signup} days ago</span>
      </div>
    </div>
  );

  const renderDispute = (item, severity) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-xs">
          Ref: {item.booking_reference}
        </Badge>
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Store className="h-4 w-4 text-purple-500" />
        <span className="font-medium">{item.vendor_name}</span>
        <span className="text-gray-400">↔</span>
        <User className="h-4 w-4 text-blue-500" />
        <span>{item.customer_name}</span>
      </div>
      <p className="text-xs text-gray-600 mt-1 line-clamp-1">
        Reason: {item.reason}
      </p>
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>{formatTimeAgo(item.hours_since_submitted)}</span>
      </div>
    </div>
  );

  const renderFeedback = (item, severity) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-xs capitalize">
          {item.feedback_type}
        </Badge>
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <p className="text-sm font-medium line-clamp-1">{item.subject}</p>
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>{formatTimeAgo(item.hours_since_submitted)}</span>
      </div>
    </div>
  );

  const renderPendingReview = (item, severity) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-3 w-3 ${i < item.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
            />
          ))}
        </div>
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Store className="h-4 w-4 text-purple-500" />
        <span className="font-medium">{item.vendor_name}</span>
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>{formatTimeAgo(item.hours_since_submitted)}</span>
      </div>
    </div>
  );

  const renderConciergeUnclaimed = (item, severity) => (
    <div className="flex-1 min-w-0" data-testid={`concierge-queue-item-${item.event_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
          <Sparkles className="h-3 w-3 mr-1" />
          Concierge intake
        </Badge>
        {severity === 'critical' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            <Flame className="h-3 w-3 mr-1" />
            Critical
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <User className="h-4 w-4 text-blue-500" />
        <span className="font-medium">{item.customer_name || 'Provisional customer'}</span>
        {item.event_type && (
          <>
            <span className="text-gray-400">·</span>
            <span className="text-gray-700">{item.event_type}</span>
          </>
        )}
      </div>
      {item.intake_description && (
        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
          {item.intake_description}
        </p>
      )}
      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
        <Clock className="h-3 w-3" />
        <span>
          Submitted {formatTimeAgo(item.hours_since_intake || 0)}
          {item.event_date ? ` · for ${item.event_date}` : ''}
        </span>
      </div>
    </div>
  );

  const renderItemContent = (item) => {
    const hours = getItemHours(item);
    const severity = getSeverity(hours);
    
    switch (item.type) {
      case 'stalled_inquiry':
        return renderStalledInquiry(item, severity);
      case 'vendor_onboarding':
        return renderVendorOnboarding(item, severity);
      case 'dispute':
        return renderDispute(item, severity);
      case 'feedback':
        return renderFeedback(item, severity);
      case 'pending_review':
        return renderPendingReview(item, severity);
      case 'concierge_unclaimed':
        return renderConciergeUnclaimed(item, severity);
      default:
        return <div>Unknown item type</div>;
    }
  };

  // Group items by type
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  // Order of categories (priority). Concierge sits right after stalled
  // inquiries — same priority bucket (2), but kept its own card so admins
  // can scan the Queue without scrolling.
  const categoryOrder = ['stalled_inquiry', 'concierge_unclaimed', 'vendor_onboarding', 'dispute', 'feedback', 'pending_review'];

  // Count critical items
  const criticalCount = items.filter(item => getSeverity(getItemHours(item)) === 'critical').length;

  // Phase 3 — surface the Concierge Queue filter chip only when the backend
  // actually returned concierge items (controlled by ENABLE_CONCIERGE_FLOW).
  // This keeps the toolbar visually empty in environments where the feature
  // flag is off, so the legacy UI is preserved.
  const conciergeAvailable = (counts.concierge_unclaimed ?? 0) > 0 || !!groupedItems.concierge_unclaimed;
  const effectiveQueueFilter = conciergeAvailable ? queueFilter : 'all';
  const visibleCategories = effectiveQueueFilter === 'concierge'
    ? ['concierge_unclaimed']
    : categoryOrder;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Attention Needed
                {counts.total > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 ml-2">
                    {counts.total}
                  </Badge>
                )}
                {criticalCount > 0 && (
                  <Badge className="bg-red-100 text-red-700 ml-1">
                    <Flame className="h-3 w-3 mr-1" />
                    {criticalCount} critical
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Unresolved items requiring admin review
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchAttentionItems}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Loading State */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">All Clear!</p>
              <p className="text-sm">No items currently require attention</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Collapsible Category Sections */
        <div className="space-y-4">
          {/* Phase 3 — Concierge Queue filter chips. Visible only when the
              backend surfaced concierge items (i.e. flag is on AND there's
              data). Toggles between "All" (default) and "Concierge only" so
              admins can focus on the Queue without dropping context. */}
          {conciergeAvailable && (
            <div
              className="flex items-center gap-2 flex-wrap"
              data-testid="concierge-queue-filter-row"
            >
              <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">View:</span>
              <Button
                size="sm"
                variant={effectiveQueueFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setQueueFilter('all')}
                className="h-7 px-3 text-xs"
                data-testid="concierge-queue-filter-all"
              >
                All ({counts.total ?? items.length})
              </Button>
              <Button
                size="sm"
                variant={effectiveQueueFilter === 'concierge' ? 'default' : 'outline'}
                onClick={() => setQueueFilter('concierge')}
                className="h-7 px-3 text-xs"
                data-testid="concierge-queue-filter-concierge"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Concierge ({counts.concierge_unclaimed ?? 0})
              </Button>
            </div>
          )}
          {visibleCategories.map(type => {
            const categoryItems = groupedItems[type];
            if (!categoryItems || categoryItems.length === 0) return null;
            
            const config = getCategoryConfig(type);
            const Icon = config.icon;
            const isExpanded = expandedSections[type];
            const categoryCriticalCount = categoryItems.filter(item => 
              getSeverity(getItemHours(item)) === 'critical'
            ).length;
            
            return (
              <Collapsible key={type} open={isExpanded} onOpenChange={() => toggleSection(type)}>
                <Card className={`${config.bgColor} ${config.borderColor} border`}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-black/5 transition-colors py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${config.iconColor}`} />
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {config.title}
                              <Badge variant="secondary" className="ml-1">
                                {categoryItems.length}
                              </Badge>
                              {categoryCriticalCount > 0 && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <Flame className="h-3 w-3 mr-0.5" />
                                  {categoryCriticalCount}
                                </Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="text-xs mt-0.5">
                              {config.description}
                            </CardDescription>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4">
                      <div className="space-y-2">
                        {categoryItems.map((item) => {
                          const hours = getItemHours(item);
                          const severity = getSeverity(hours);
                          const severityStyles = getSeverityStyles(severity);
                          
                          return (
                            <div
                              key={item.id}
                              className={`p-3 rounded-lg border border-gray-200 hover:border-amber-300 transition-all ${severityStyles.bg} ${severityStyles.border}`}
                            >
                              <div className="flex items-start gap-3">
                                <div 
                                  className="flex-1 min-w-0 cursor-pointer"
                                  onClick={() => handleItemClick(item)}
                                >
                                  {renderItemContent(item)}
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-1">
                                  {/* Action buttons based on item type */}
                                  {item.type === 'vendor_onboarding' && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 px-2 text-gray-500 hover:text-emerald-600"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/admin/dashboard?tab=users&search=${encodeURIComponent(item.vendor_name)}`);
                                        }}
                                        title="View vendor profile"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 px-2 text-gray-500 hover:text-green-600"
                                        onClick={(e) => dismissItem(e, item.id)}
                                        title="Mark as addressed"
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  {item.type === 'stalled_inquiry' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-gray-500 hover:text-green-600"
                                      onClick={(e) => dismissItem(e, item.id)}
                                      title="Mark as addressed"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <ChevronRight 
                                    className="w-4 h-4 text-gray-400 cursor-pointer" 
                                    onClick={() => handleItemClick(item)}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAttentionTab;
